import React, { ChangeEvent, PureComponent } from 'react';
import { inject, observer } from 'mobx-react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import { UiStore } from '../../core/ui-store';
import { UserStore } from '../../core/user-store';
import { setPostLoginRedirectCookie } from '../../core/authentication';
import { PAGES_BASE_PATHNAMES } from '../../core/consts';
import history from '../../core/history';
import { OktaScopes, OktaScopesConfig } from '../../core/okta-auth-api.consts';
import { OktaScopeConfig } from '../../core/okta-auth-api.types';

import './DashboardPage.scss';
import { track, TrackedEvent } from '../../core/analytics';
import DashboardAppButtons from './dashboard-app-buttons';
import { Tab, Tabs, Tooltip } from '@mui/material';
import { sleepByMs } from '../../core/utils';
import { UsageExampleLanguages, UsageExampleLanguagesDisplaySettings } from '../../core/ui-store.consts';

const CopyButtonsTooltipDurationMs: number = 4000;

interface StoresProps {
  userStore?: UserStore;
  uiStore?: UiStore;
}

type Props = StoresProps;

interface State {
  showAccessTokenCopied: boolean;
  showRefreshTokenCopied: boolean;
  showUsageExampleCopied: boolean;
}

const DashboardPage = inject(
  'userStore',
  'uiStore'
)(
  observer(
    class DashboardPage extends PureComponent<Props, State> {
      isExisting: boolean;
      constructor(props) {
        super(props);
        this.state = {
          showAccessTokenCopied: false,
          showRefreshTokenCopied: false,
          showUsageExampleCopied: false,
        };
      }

      componentDidMount(): void {
        this.isExisting = true;
      }

      componentWillUnmount(): void {
        this.isExisting = false;
      }

      handleGenerateTokensClick = (oktaScope: OktaScopes) => {
        const { userApiTokens, setRequestedOktaScope } = this.props.userStore;
        /*
        if (userApiTokens[oktaScope]) {
          return;
        }
        */

        track(TrackedEvent.apiAccessGenerateAppTokensClicked, { value: oktaScope });

        setPostLoginRedirectCookie();

        setTimeout(() => {
          history.push(PAGES_BASE_PATHNAMES.Login);
        }, 0);
      };

      handleTabChange = (e: ChangeEvent, langIndex: number) => {
        const lang: UsageExampleLanguages = Object.keys(UsageExampleLanguagesDisplaySettings)[
          langIndex
        ] as UsageExampleLanguages;
        this.props.uiStore.setUsageExampleLanguage(lang);
        track(TrackedEvent.apiAccessUsageExampleLanguageChanged, { value: lang });
      };

      handleCopyButtonClick = (token: string) => {
        if (!token) {
          return;
        }
        const el: HTMLTextAreaElement = document.createElement('textarea');
        el.value = token;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      };

      getUsageExampleString = (): string => {
        const { userApiTokens, requestedOktaScope } = this.props.userStore;
        const { usageExampleLanguage } = this.props.uiStore;
        let contentStr: string = '';

        switch (usageExampleLanguage) {
          case UsageExampleLanguages.Shell:
            contentStr = `$ export REFRESH_TOKEN=${userApiTokens[requestedOktaScope].refreshToken}
$ TOKEN=$(curl 'https://external.getnexar.com/dev-portal/refresh-token' \\
    --data "refresh_token=$REFRESH_TOKEN" \\
    --compressed | jq -r ".access_token")`;
            break;
          case UsageExampleLanguages.Javascript:
            contentStr = `var response = await fetch("https://external.getnexar.com/dev-portal/refresh-token", {
  "headers": { 'content-type': 'application/x-www-form-urlencoded' },
  "body": "refresh_token=${userApiTokens[requestedOktaScope].refreshToken}",
  "method": "POST",
});
var result = await response.json();
console.log(result);`;
            break;
          case UsageExampleLanguages.Python:
            contentStr = `import requests
response = requests.post('https://external.getnexar.com/dev-portal/refresh-token',
  headers={'content-type': 'application/x-www-form-urlencoded'},
  data={'refresh_token': '${userApiTokens[requestedOktaScope].refreshToken}'}
)
print(response.json())`;
        }
        return contentStr;
      };

      renderUsageExample = () => {
        const { usageExampleLanguage } = this.props.uiStore;

        const contentStr: string = this.getUsageExampleString();

        return (
          <SyntaxHighlighter
            language={usageExampleLanguage.toLocaleLowerCase()}
            style={dracula}
            className="syntax-highlighter"
            wrapLines
            wrapLongLines
          >
            {contentStr}
          </SyntaxHighlighter>
        );
      };

      render() {
        const { userData, displayedOktaScopes, userApiTokens, requestedOktaScope, setRequestedOktaScope } =
          this.props.userStore;
        const { usageExampleLanguage } = this.props.uiStore;
        const { showAccessTokenCopied, showRefreshTokenCopied, showUsageExampleCopied } = this.state;

        if (!userData || !userData.id || !requestedOktaScope) {
          return null;
        }

        const requestedOktaScopeConfig: OktaScopeConfig = OktaScopesConfig[requestedOktaScope];

        return (
          <React.Fragment>
            <div className="page-title">Welcome {userData.$first_name},</div>
            <div className="page-subtitle">Please choose the product for which you’d like to generate a token</div>
            <div className="app-buttons-container">
              <DashboardAppButtons
                displayedOktaScopes={displayedOktaScopes}
                requestedOktaScope={requestedOktaScope}
                setRequestedOktaScope={setRequestedOktaScope}
              />
            </div>
            <section>
              {!userApiTokens[requestedOktaScope] ? (
                <button
                  className="btn btn-primary"
                  disabled={!!userApiTokens[requestedOktaScope]}
                  onClick={() => {
                    this.handleGenerateTokensClick(requestedOktaScope);
                  }}
                >
                  {`Generate ${requestedOktaScopeConfig.title} API tokens`}
                </button>
              ) : (
                <React.Fragment>
                  <div className="token-label">Access Token</div>
                  <div className="token-content">
                    <div>{userApiTokens[requestedOktaScope].accessToken}</div>
                    <Tooltip
                      arrow
                      title={<div>Access token copied to your clipboard</div>}
                      placement="right"
                      disableHoverListener={true}
                      disableTouchListener={true}
                      disableInteractive={true}
                      open={!!showAccessTokenCopied}
                    >
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          this.handleCopyButtonClick(userApiTokens[requestedOktaScope].accessToken);
                          track(TrackedEvent.apiAccessCopyAccessTokenClicked, { value: requestedOktaScope });
                          if (!this.isExisting) {
                            return;
                          }
                          this.setState(
                            {
                              showAccessTokenCopied: true,
                              showRefreshTokenCopied: false,
                              showUsageExampleCopied: false,
                            },
                            async () => {
                              await sleepByMs(CopyButtonsTooltipDurationMs);
                              if (!this.isExisting) {
                                return;
                              }
                              this.setState({
                                showAccessTokenCopied: false,
                              });
                            }
                          );
                        }}
                      >
                        Copy
                      </button>
                    </Tooltip>
                  </div>
                  <div className="token-label">Refresh Token</div>
                  <div className="token-content">
                    <div>{userApiTokens[requestedOktaScope].refreshToken}</div>
                    <Tooltip
                      arrow
                      title={<div>Refresh token copied to your clipboard</div>}
                      placement="right"
                      disableHoverListener={true}
                      disableTouchListener={true}
                      disableInteractive={true}
                      open={!!showRefreshTokenCopied}
                    >
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          this.handleCopyButtonClick(userApiTokens[requestedOktaScope].refreshToken);
                          track(TrackedEvent.apiAccessCopyRefreshTokenClicked, { value: requestedOktaScope });
                          if (!this.isExisting) {
                            return;
                          }
                          this.setState(
                            {
                              showAccessTokenCopied: false,
                              showRefreshTokenCopied: true,
                              showUsageExampleCopied: false,
                            },
                            async () => {
                              await sleepByMs(CopyButtonsTooltipDurationMs);
                              if (!this.isExisting) {
                                return;
                              }
                              this.setState({
                                showRefreshTokenCopied: false,
                              });
                            }
                          );
                        }}
                      >
                        Copy
                      </button>
                    </Tooltip>
                  </div>
                  <div className="token-sublabel">Usage example:</div>
                  <Tabs
                    value={
                      usageExampleLanguage
                        ? Object.keys(UsageExampleLanguagesDisplaySettings).findIndex(
                            (lang) => lang === usageExampleLanguage
                          )
                        : null
                    }
                    onChange={this.handleTabChange}
                  >
                    {Object.keys(UsageExampleLanguagesDisplaySettings).map((lang) => {
                      const Icon = UsageExampleLanguagesDisplaySettings[lang].icon;
                      return (
                        <Tab
                          key={lang}
                          label={UsageExampleLanguagesDisplaySettings[lang].title}
                          id={lang}
                          icon={<Icon />}
                          iconPosition={'start'}
                        />
                      );
                    })}
                  </Tabs>
                  <div className="usage-example-content">
                    {this.renderUsageExample()}
                    <Tooltip
                      arrow
                      title={<div>Usage example copied to your clipboard</div>}
                      placement="right"
                      disableHoverListener={true}
                      disableTouchListener={true}
                      disableInteractive={true}
                      open={!!showUsageExampleCopied}
                    >
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          this.handleCopyButtonClick(this.getUsageExampleString());
                          track(TrackedEvent.apiAccessCopyUsageExampleClicked, {
                            value: requestedOktaScope,
                            language: this.props.uiStore.usageExampleLanguage,
                          });
                          if (!this.isExisting) {
                            return;
                          }
                          this.setState(
                            {
                              showAccessTokenCopied: false,
                              showRefreshTokenCopied: false,
                              showUsageExampleCopied: true,
                            },
                            async () => {
                              await sleepByMs(CopyButtonsTooltipDurationMs);
                              if (!this.isExisting) {
                                return;
                              }
                              this.setState({
                                showUsageExampleCopied: false,
                              });
                            }
                          );
                        }}
                      >
                        Copy
                      </button>
                    </Tooltip>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      this.handleGenerateTokensClick(requestedOktaScope);
                    }}
                  >
                    {`Regenerate ${requestedOktaScopeConfig.title} API tokens`}
                  </button>
                </React.Fragment>
              )}
            </section>
          </React.Fragment>
        );
      }
    }
  )
);

export default DashboardPage;
